/**
 * Socket.IO client factory for the Hostesa realtime control-plane.
 *
 * Creates a single Socket.IO instance that connects to the `/app` namespace.
 * Authentication is carried in the Socket.IO `auth` payload — NEVER in the
 * query string, which would expose the session token in server logs.
 *
 * Usage:
 *   const socket = createRealtimeClient({ url: 'https://rt.example.com', getToken });
 */

import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';

export interface RealtimeClientOptions {
  /** Base URL of the realtime gateway (e.g. "https://rt.example.com"). */
  url: string;
  /**
   * Async getter for the current session token. Called on every connect and
   * reconnect so the freshest token is always used.
   */
  getToken: () => string | Promise<string>;
}

export type RealtimeSocket = Socket;

/**
 * Creates and returns a Socket.IO instance connected to the `/app` namespace.
 *
 * The socket does NOT connect immediately — connection starts when the caller
 * (or the provider) calls `socket.connect()`. This keeps the factory pure and
 * testable.
 *
 * Auth flow:
 *   - The `auth` callback is called on every handshake (initial + reconnects).
 *   - It resolves the token just-in-time so an expired token is replaced before
 *     the reconnect handshake rather than after.
 *
 * `connect_error` with reason `"unauthorized"` means the token was rejected —
 * callers should refresh the session and call `socket.connect()` again.
 */
export function createRealtimeClient({ url, getToken }: RealtimeClientOptions): RealtimeSocket {
  const socket = io(`${url}/app`, {
    // Prefer WebSocket; fall back to polling only if WS is unavailable.
    transports: ['websocket', 'polling'],
    // Auth callback is invoked on every connect / reconnect so we always use
    // the freshest token without manually reconnecting after a refresh.
    auth: (cb: (data: Record<string, unknown>) => void) => {
      void Promise.resolve(getToken()).then((token) => {
        cb({ token });
      });
    },
    // Reconnection is managed by socket.io-client with exponential back-off.
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    // Do not auto-connect — let the provider control the connection lifecycle.
    autoConnect: false,
  });

  return socket;
}
